"use client";

import { css } from "styled-components";

import { isBrowser, isDesktop, isIOS, isMobile } from "./platform";

import * as VARS from "./styles/vars";
import { getViewportInfo } from "./dom";
import { useGlobalStore } from "@/mono/store/global";

export const mqMobile = `(max-width: ${VARS.breakpointTablet - 1}px)`;
export const mediaMobile = (content) =>
  css`
    @media ${mqMobile} {
      ${content}
    }
  `;

export const mqMobileSmallHeight = `(max-height: ${VARS.breakpointMobileSmallHeight}px)`;
export const mediaMobileSmallHeight = (content) =>
  css`
    @media ${mqMobileSmallHeight} {
      ${content}
    }
  `;

export const mqTablet = `(min-width: ${VARS.breakpointTablet}px)`;
export const mediaTablet = (content) =>
  css`
    @media ${mqTablet} {
      ${content}
    }
  `;

export const mqTabletDown = `(max-width: ${VARS.breakpointDesktop - 1}px)`;
export const mediaTabletDown = (content) =>
  css`
    @media ${mqTabletDown} {
      ${content}
    }
  `;

export const mqTabletOnly = `(min-width: ${
  VARS.breakpointTablet
}px) and (max-width: ${VARS.breakpointDesktop - 1}px)`;
export const mediaTabletOnly = (content) =>
  css`
    @media ${mqTabletOnly} {
      ${content}
    }
  `;

export const mqDesktop = `(min-width: ${VARS.breakpointDesktop}px) and (min-height: ${VARS.desktopMinHeight}px)`;
export const mediaDesktop = (content) =>
  css`
    @media ${mqDesktop} {
      ${content}
    }
  `;

const mqDesktopLarge = `(min-width: ${VARS.breakpointDesktopLarge}px)`;
export const mediaDesktopLarge = (content) =>
  css`
    @media ${mqDesktopLarge} {
      ${content}
    }
  `;

export const mqDesktopWide = `(min-width: ${VARS.breakpointDesktop}px) and (min-aspect-ratio: ${VARS.desktopWideAspectRatio})`;
export const mediaDesktopWide = (content) =>
  css`
    @media ${mqDesktopWide} {
      ${content}
    }
  `;

export const mqLandscape = "(orientation: landscape)";
export const mediaLandscape = (content) =>
  css`
    @media ${mqLandscape} {
      ${content}
    }
  `;

export const mqPortrait = "(orientation: portrait)";
export const mediaPortrait = (content) =>
  css`
    @media ${mqPortrait} {
      ${content}
    }
  `;

export const matchMobile = {
  match: isBrowser() && window.matchMedia(mqMobile),
  scalableFontSize: VARS.mobileScalableFontSize,
  minFontSize: VARS.minFontSize.mobile,
  setVh: false,
};

export const matchMobileSmallHeight = {
  match: isBrowser() && window.matchMedia(mqMobileSmallHeight),
  scalableFontSize: VARS.mobileSmallHeightScalableFontSize,
  minFontSize: VARS.minFontSize.mobile,
  setVh: false,
};

export const matchTablet = {
  match: isBrowser() && window.matchMedia(mqTablet),
  scalableFontSize: VARS.tabletScalableFontSize,
  minFontSize: VARS.minFontSize.tablet,
  setVh: false,
};

export const matchDesktop = {
  match: isBrowser() && window.matchMedia(mqDesktop),
  scalableFontSize: VARS.desktopScalableFontSize,
  minFontSize: VARS.minFontSize.desktop,
  setVh: false,
};

export const matchDesktopWide = {
  match: isBrowser() && window.matchMedia(mqDesktopWide),
  scalableFontSize: VARS.desktopWideScalableFontSize,
  minFontSize: VARS.minFontSize.desktop,
  setVh: true,
};

export const isMobileLayout = (overrideWidth) => {
  const width =
    typeof overrideWidth === "number"
      ? overrideWidth
      : useGlobalStore.getState().windowSize.width;
  if (!width || width === 0) return false;
  return isMobile() || width < VARS.breakpointTablet;
};

export const isTabletOnlyLayout = (overrideWidth) => {
  const width =
    typeof overrideWidth === "number"
      ? overrideWidth
      : useGlobalStore.getState().windowSize.width;
  if (!width || width === 0) return false;
  return width < VARS.breakpointDesktop && width >= VARS.breakpointTablet;
};

export const isTabletOrSmallerLayout = (overrideWidth) => {
  const width =
    typeof overrideWidth === "number"
      ? overrideWidth
      : useGlobalStore.getState().windowSize.width;
  if (!width || width === 0) return false;
  // Align with CSS mediaTabletDown: (max-width: breakpointDesktop - 1px)
  return width < VARS.breakpointDesktop;
};

export const setScalableFontSize = () => {
  // iOS doesn't scale non-px units automatically when zooming
  const setDocumentZoom = (value) =>
    (isDesktop() || isIOS()) && process.env.ALLOW_USER_ZOOM
      ? `calc(var(--zoomLevel, 1) * ${value})`
      : value;

  const result = css`
    font-size: ${setDocumentZoom(`${VARS.mobileScalableFontSize}vw`)};

    ${mediaMobileSmallHeight(css`
      font-size: ${setDocumentZoom(
        `${VARS.mobileSmallHeightScalableFontSize}vw`
      )};
    `)}

    ${mediaTablet(css`
      font-size: ${setDocumentZoom(`${VARS.tabletScalableFontSize}vw`)};
    `)}

    ${mediaDesktop(css`
      font-size: ${setDocumentZoom(`${VARS.desktopScalableFontSize}vw`)};
    `)}

    ${mediaDesktopWide(css`
      font-size: ${setDocumentZoom(`${VARS.desktopWideScalableFontSize}vw`)};
    `)}
  `;

  return result;
};
